/**
 * Système de Rôles Unifié - Nazandoo Marketplace
 * 
 * Ce fichier définit les types pour le nouveau système de rôles à deux niveaux:
 * 1. RÔLE SYSTÈME (system_role) - Obligatoire, définit les permissions globales
 * 2. RÔLE ORGANISATIONNEL (org_role) - Optionnel, définit la fonction dans l'entreprise
 */

/**
 * Rôle Système - Définit les permissions globales dans l'application
 */
export type SystemRole = 
  | 'super_admin'  // Accès complet, ne peut pas être suspendu
  | 'admin'        // Administration complète (sauf gestion super_admin)
  | 'hr'           // Ressources humaines, gestion employés
  | 'vendor'       // Vendeur, gestion produits/commandes
  | 'relay'        // Point relais, gestion stock/retraits
  | 'client';      // Client standard, achats uniquement

/**
 * Rôle Organisationnel - Définit la fonction au sein de l'entreprise (optionnel)
 */
export type OrgRole = 
  | 'owner'        // Propriétaire de l'entreprise
  | 'manager'      // Gestionnaire/Chef
  | 'supervisor'   // Superviseur
  | 'employee'     // Employé standard
  | 'contractor'   // Contractant/Freelance
  | 'intern';      // Stagiaire

/**
 * Ordre de priorité des rôles système (du plus élevé au plus bas)
 */
export const SYSTEM_ROLE_PRIORITY: Record<SystemRole, number> = {
  super_admin: 1,
  admin: 2,
  hr: 3,
  vendor: 4,
  relay: 5,
  client: 6
};

/**
 * Labels français pour les rôles système
 */
export const SYSTEM_ROLE_LABELS: Record<SystemRole, string> = {
  super_admin: 'Super Admin',
  admin: 'Administrateur',
  hr: 'Ressources Humaines',
  vendor: 'Vendeur',
  relay: 'Point Relais',
  client: 'Client'
};

/**
 * Labels français pour les rôles organisationnels
 */
export const ORG_ROLE_LABELS: Record<OrgRole, string> = {
  owner: 'Propriétaire',
  manager: 'Gestionnaire',
  supervisor: 'Superviseur',
  employee: 'Employé',
  contractor: 'Contractant',
  intern: 'Stagiaire'
};

/**
 * Descriptions des rôles système
 */
export const SYSTEM_ROLE_DESCRIPTIONS: Record<SystemRole, string> = {
  super_admin: 'Accès complet à toutes les fonctionnalités',
  admin: 'Administration complète du système',
  hr: 'Gestion des employés et ressources humaines',
  vendor: 'Gestion des produits et commandes',
  relay: 'Gestion du stock et des retraits',
  client: 'Achats et gestion de commandes personnelles'
};

/**
 * Couleurs pour les badges de rôles système
 */
export const SYSTEM_ROLE_COLORS: Record<SystemRole, string> = {
  super_admin: 'bg-purple-100 text-purple-800 border-purple-300',
  admin: 'bg-red-100 text-red-800 border-red-300',
  hr: 'bg-orange-100 text-orange-800 border-orange-300',
  vendor: 'bg-blue-100 text-blue-800 border-blue-300',
  relay: 'bg-green-100 text-green-800 border-green-300',
  client: 'bg-gray-100 text-gray-800 border-gray-300'
};

/**
 * Icônes Lucide pour les rôles système
 */
export const SYSTEM_ROLE_ICONS = {
  super_admin: 'Crown',
  admin: 'Shield',
  hr: 'Users',
  vendor: 'Store',
  relay: 'MapPin',
  client: 'UserCheck'
} as const;

/**
 * Vérifie si un rôle est un rôle admin (admin ou super_admin)
 */
export const isAdminRole = (role: SystemRole): boolean => {
  return role === 'admin' || role === 'super_admin';
};

/**
 * Vérifie si un rôle est un rôle super_admin
 */
export const isSuperAdminRole = (role: SystemRole): boolean => {
  return role === 'super_admin';
};

/**
 * Compare deux rôles système et retourne le plus élevé
 */
export const getHighestRole = (role1: SystemRole, role2: SystemRole): SystemRole => {
  return SYSTEM_ROLE_PRIORITY[role1] < SYSTEM_ROLE_PRIORITY[role2] ? role1 : role2;
};

/**
 * Permissions par rôle système
 */
export const SYSTEM_ROLE_PERMISSIONS: Record<SystemRole, string[]> = {
  super_admin: ['*'], // Toutes les permissions
  admin: [
    'users.read', 'users.create', 'users.update', 'users.delete',
    'products.manage', 'orders.manage', 'relays.manage',
    'analytics.view', 'config.update'
  ],
  hr: [
    'employees.read', 'employees.create', 'employees.update', 'employees.delete',
    'analytics.view'
  ],
  vendor: [
    'products.own.manage', 'orders.own.manage', 'analytics.own.view'
  ],
  relay: [
    'stock.manage', 'orders.relay.manage', 'pickups.manage'
  ],
  client: [
    'products.view', 'orders.own.create', 'orders.own.view'
  ]
};

/**
 * Vérifie si un rôle a une permission spécifique
 */
export const hasPermission = (role: SystemRole, permission: string): boolean => {
  const permissions = SYSTEM_ROLE_PERMISSIONS[role];
  return permissions.includes('*') || permissions.includes(permission);
};

/**
 * Interface Profile mise à jour avec le nouveau système de rôles
 */
export interface ProfileWithRoles {
  id: string;
  system_role: SystemRole;
  org_role?: OrgRole | null;
  full_name: string | null;
  email?: string | null;
  phone: string | null;
  avatar_url: string | null;
  vendor_status?: 'pending' | 'approved' | 'rejected' | null;
  is_suspended?: boolean;
  account_status?: string;
  created_at: string;
  updated_at: string;
}
